/**
 * Responsibility: describe the farmland biome — its near-level ground, the worked rows on it, and the
 * occasional farmstead.
 *
 * ## What the first version got wrong
 *
 * It drew field *boundaries* and no rows at all, in a right-angled survey grid, in a yellow-green. Flown, it
 * read as "yellow lines on the ground" rather than as farmland, and the grid marched straight up mountain
 * flanks. Three separate mistakes, each fixed here:
 *
 * 1. **Rows, and they are raised.** A field is filled with closely spaced ridges of worked ground. Rows are
 *    the texture that says *worked*; a boundary alone only says *edge*. Raised rather than drawn flat,
 *    because a line lying on the surface reads as paint.
 * 2. **Rows follow the contour, not the compass.** Each field takes its direction from the terrain's
 *    gradient — a contour runs perpendicular to it — which is what contour ploughing physically is. Adjacent
 *    fields on different ground therefore run at different angles, and nothing is at right angles to
 *    anything.
 * 3. **Nothing is farmed on a slope.** `MAX_ROW_SLOPE` refuses ground steeper than about 17 degrees, so the
 *    pattern stops where a hillside begins instead of climbing it.
 *
 * ## Why a farmstead is worth the geometry
 *
 * Rows alone are ambiguous — they could be anything striped. A house, a barn with a pitched roof and a silo
 * tell the viewer what they are looking at, and every other building in the world is flat-topped, so a
 * gable roof reads as instantly not-a-tower.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { createFbm } from '../noise.js';
import { createLocalRng, deriveSeed } from '../rng.js';
import { createWindFarm, createWindFarmClearance } from '../windFarm.js';
import { FARMLAND, FARMLAND_LINE_PALETTE, PALETTE, degToRad } from '../../config.js';

/** Linear interpolation, for turning a 0..1 roll into a range. */
function lerp(from, to, t) {
  return from + (to - from) * t;
}

/**
 * Range of field indices whose **centres** fall inside a span.
 *
 * ## One tile owns each field
 *
 * Field size is not a multiple of tile size, so fields straddle tile edges constantly. Selecting every field
 * that *overlaps* a tile meant a straddling field was emitted by both of its tiles — every row drawn twice, at
 * double brightness, which under bloom is very visible. It also silently doubled the geometry budget for those
 * fields.
 *
 * A field therefore belongs to whichever tile contains its centre, and simply extends past the edge when it
 * has to. Tile bounding spheres come from real vertex positions, so overhang is harmless. This is the same
 * ownership rule the city's blocks follow, and the arithmetic is identical: the centre of field `i` is
 * `(i + 0.5) * size`, so requiring that to land in `[start, start + length)` gives the bounds below.
 *
 * Getting this off by one either duplicates a row of fields along every seam or leaves a gap in the pattern.
 */
function fieldRange(start, length, size) {
  return {
    first: Math.ceil(start / size - 0.5),
    last: Math.ceil((start + length) / size - 0.5) - 1,
  };
}

export const farmland = {
  id: 'farmland',
  label: 'Farmland',

  palette: PALETTE.farmland,

  /*
    Rows and hedges get their own gradient. The ground is deliberately muted so the worked texture carries
    the biome; one shared gradient would leave both the same colour and the pattern would disappear.
  */
  structurePalette: FARMLAND_LINE_PALETTE,

  suitabilityBias: FARMLAND.SUITABILITY_BIAS,

  /**
   * Build the ground elevation function.
   *
   * Deliberately low relief: this is the *flat* farmland, kept separate from the version over rolling hills.
   * Not level, though — a completely flat plane reads as a void in wireframe, and the slight roll is also what
   * gives the contour direction something to follow.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number) => number} Elevation in world units, always >= 0.
   */
  createElevation(seed) {
    const field = createFbm(seed, 'elevation:farmland', {
      octaves: FARMLAND.OCTAVES,
      baseFrequency: 1 / FARMLAND.WAVELENGTH,
      persistence: FARMLAND.PERSISTENCE,
      lacunarity: FARMLAND.LACUNARITY,
    });

    return function sampleFarmlandElevation(x, z) {
      // Remapped to 0..1 so this biome can never contribute a negative elevation to the blend.
      const unit = (field(x, z) + 1) / 2;
      return unit * FARMLAND.MAX_ELEVATION;
    };
  },

  maxElevation: FARMLAND.MAX_ELEVATION,

  /**
   * Build the structure placement function.
   *
   * @param {number} seed - Base world seed.
   * @returns {(region: object, context: object) => Array<object>}
   */
  createStructures(seed) {
    /*
      A dedicated seed, so the field layout is statistically independent of the biome's own suitability field
      and of its ground. Sharing one would line the fields up with wherever the ground happens to rise.
    */
    const patternSeed = deriveSeed(seed, 'structures:farmland');

    const size = FARMLAND.FIELD_SIZE;
    const angleJitter = degToRad(FARMLAND.ROW_ANGLE_JITTER);

    /*
      The wind farms this biome hosts, and the test that keeps its own farmyards out of them. A biome never sees
      its own reservation — self-consultation would reject everything — so the one case that genuinely conflicts
      is checked here explicitly.
    */
    const windFarms = createWindFarm(seed, 'farmland');
    const insideAWindFarm = createWindFarmClearance(seed, 'farmland');

    /**
     * @param {{originX: number, originZ: number, size: number}} region - Tile bounds.
     * @param {object} context
     * @param {(x: number, z: number) => number} context.sampleWeight - Farmland biome weight.
     * @param {(x: number, z: number) => number} context.sampleGround - Drawn surface height.
     * @param {(x: number, z: number) => {elevation: number, slope: number, gradientX: number,
     *   gradientZ: number}} context.sampleGroundDetail
     * @param {(x: number, z: number, radius: number) => boolean} [context.isReserved]
     * @returns {Array<object>}
     */
    return function collectStructures(
      region,
      { sampleWeight, sampleWeights, sampleGround, sampleGroundDetail, isReserved },
    ) {
      const xRange = fieldRange(region.originX, region.size, size);
      const zRange = fieldRange(region.originZ, region.size, size);

      const structures = [];

      /** Memo for the wind farm test, which every farmyard in the tile asks. Lives for this build only. */
      const windCache = new Map();

      /**
       * Whether a farmyard may stand at a point: clear of other biomes, and clear of our own wind farms.
       *
       * **The second half is here because a biome never sees its own reservation.** Self-consultation would
       * reject everything, so the one case that genuinely conflicts — a barn at the foot of a turbine — is
       * checked directly.
       *
       * Fields, rows and hedgerows deliberately do not use this. A turbine standing in a crop is what a real one
       * does, and refusing whole 520-unit fields around a row a few thousand units long would leave a bare
       * corridor where there should be farmland.
       */
      const blocked = (x, z, radius) =>
        isReserved?.(x, z, radius) === true ||
        insideAWindFarm(x, z, radius, { sampleWeight, cache: windCache });

      /**
       * Combined weight of the biomes farmland refuses to share ground with.
       *
       * Farming is an exclusive land use in a way trees and towers are not: a field inside a city is not a
       * transition, it is a mistake, and a ploughed row part-way up a mountain is the same. So farmland stops
       * *before* a rival region rather than thinning into it.
       */
      const rivalWeightAt = (x, z) => {
        if (typeof sampleWeights !== 'function') return 0;

        let total = 0;
        for (const entry of sampleWeights(x, z)) {
          if (FARMLAND.EXCLUSIVE_AGAINST.includes(entry.id)) total += entry.weight;
        }
        return total;
      };

      /**
       * The strongest rival claim anywhere over a whole field.
       *
       * Sampled at the centre *and* the four corners, taking the worst. Checking the centre alone left rows
       * reaching into ground where a city was three times over the limit: a field is 520 units across, so its
       * far edge can be well inside a rival region while its middle is still clear.
       *
       * Five samples per field, and only about sixteen fields to a tile — negligible beside the thousands of
       * terrain samples a tile already costs.
       */
      const worstRivalOverField = (minX, minZ) => {
        let worst = rivalWeightAt(minX + size / 2, minZ + size / 2);

        for (const [cx, cz] of [
          [minX, minZ],
          [minX + size, minZ],
          [minX, minZ + size],
          [minX + size, minZ + size],
        ]) {
          worst = Math.max(worst, rivalWeightAt(cx, cz));
          if (worst > FARMLAND.MAX_RIVAL_WEIGHT) return worst;
        }

        return worst;
      };

      /**
       * Whether any part of a field's ground is reserved by another biome — which above all means **under water**.
       *
       * **The rows and hedgerows were tested nowhere at all**, so a whole 520-unit field of ploughed ridges could run
       * straight across a lake. Only the farmstead yard was checked, and even that left the barn and the silo, which are
       * placed at offsets from it, untested.
       *
       * Nine points on a three-by-three grid over the field, each with a quarter-field radius, rather than the centre
       * alone: at 520 units across, a field's far edge can be deep in a lake while its middle is dry. The circles
       * overlap enough that nothing wider than about a hundred units can hide between them, and a lake is hundreds.
       *
       * Cheap in practice despite the count. Almost every field is nowhere near water, and each reservation answers with
       * one noise sample before doing anything expensive — and the search stops at the first hit.
       */
      const fieldIsReserved = (minX, minZ) => {
        if (typeof isReserved !== 'function') return false;

        const reach = size / 4;

        for (const fx of [0, 0.5, 1]) {
          for (const fz of [0, 0.5, 1]) {
            if (isReserved(minX + size * fx, minZ + size * fz, reach)) return true;
          }
        }

        return false;
      };

      for (let fieldX = xRange.first; fieldX <= xRange.last; fieldX += 1) {
        for (let fieldZ = zRange.first; fieldZ <= zRange.last; fieldZ += 1) {
          const minX = fieldX * size;
          const minZ = fieldZ * size;
          const centerX = minX + size / 2;
          const centerZ = minZ + size / 2;

          const weight = sampleWeight(centerX, centerZ);
          if (weight <= 0) continue;

          /*
            Refuse ground a rival biome has any real claim on. Checked before the field's rolls are drawn, and
            deliberately so: an unfarmed field has no layout at all, so there is nothing whose randomness could
            be shifted. That differs from the slope and reservation checks below, which reject *part* of a
            field's contents and therefore have to come after every roll.
          */
          if (worstRivalOverField(minX, minZ) > FARMLAND.MAX_RIVAL_WEIGHT) continue;

          /*
            One stream per field, keyed by field index rather than by tile, so the pattern does not depend on
            which tile is being built or the order tiles are visited in.

            Roll order is part of the world's identity. Reordering these, or inserting one before an existing
            one, changes every farm at every seed. Append only.
          */
          const random = createLocalRng(patternSeed, fieldX, fieldZ);

          const angleRoll = random();
          const flatAngleRoll = random();
          const westHedgeRoll = random();
          const northHedgeRoll = random();
          const farmsteadRoll = random();
          const farmsteadPlaceX = random();
          const farmsteadPlaceZ = random();
          const farmsteadFacingRoll = random();
          const windpumpRoll = random();
          const windpumpPlaceX = random();
          const windpumpPlaceZ = random();
          const windpumpFacing = random();

          /*
            Nothing is farmed on a slope. Sampled at the field centre and applied to the whole field, so a
            field is either worked or it is not — a partially ploughed field would read as an artefact, and
            this is what stops rows climbing a mountainside.
          */
          const centre = sampleGroundDetail(centerX, centerZ);
          if (centre.slope > FARMLAND.MAX_ROW_SLOPE) continue;

          /*
            Row direction from the terrain's contour: a contour runs perpendicular to the gradient, which is
            what contour ploughing follows. On ground flat enough that the gradient points nowhere in
            particular, each field picks its own direction instead — which is what flat farmland looks like
            anyway, and it stops neighbouring fields swinging wildly on numerical noise.
          */
          const contourAngle =
            centre.slope > FARMLAND.CONTOUR_MIN_SLOPE
              ? Math.atan2(centre.gradientX, -centre.gradientZ)
              : flatAngleRoll * Math.PI * 2;

          const rowAngle = contourAngle + (angleRoll * 2 - 1) * angleJitter;

          const along = { x: Math.cos(rowAngle), z: Math.sin(rowAngle) };
          const across = { x: -along.z, z: along.x };

          /*
            Rows thin out with biome weight, so farmland fades in as looser and looser working rather than
            stopping at a hard line. The same thinning-not-fading rule the city and forest follow.
          */
          const rowFraction = Math.min(1, weight);
          const halfSpan = size / 2;
          const rowCount = Math.floor((size * rowFraction) / FARMLAND.ROW_SPACING);

          /*
            A field is either properly worked or not worked at all.

            Thinning used to take a fringe field down to one or two rows, and a couple of isolated ridges read
            as stray lines for no apparent reason — the same failure as the whole first version, in miniature.
            Skipping the field gives farmland a ragged edge of unworked ground instead of orphan rows.

            Placed after the rolls are drawn so it cannot shift any other field's layout, and before the
            hedgerows and farmstead so an unworked field gets none of those either — a lone barn in an empty
            field would be its own version of the same problem.
          */
          if (rowCount < FARMLAND.MIN_ROWS) continue;

          /*
            And a field is not worked at all if another biome has reserved any of its ground — which in practice means a
            lake or a river crosses it.

            **Whole-field, not per-row**, and that is the same judgement `MIN_ROWS` above makes: a field with its rows
            clipped around a shoreline reads as an artefact, where farmland simply stopping short of the water reads as
            land nobody ploughs. It also matches what this biome already does with rival regions — it stops *before* them
            rather than thinning into them.

            Placed with the other whole-field rejections, after the rolls are drawn, so it cannot shift any other field's
            layout. Everything below is skipped with it: rows, hedgerows, the farmstead and the windpump, because a lone
            barn standing in a lake is the bug this exists to prevent.
          */
          if (fieldIsReserved(minX, minZ)) continue;

          for (let i = 0; i < rowCount; i += 1) {
            /*
              Rows centred on the field, so thinning removes them from the outside in and a weak field reads
              as a smaller worked patch rather than as a field with a gap down the middle.
            */
            const offset = (i - (rowCount - 1) / 2) * FARMLAND.ROW_SPACING;

            /*
              Length shortened toward the field's edges so the worked area is a rounded patch rather than a
              square. Square fields at every angle looked mechanical, and real fields are bounded by the land.
            */
            const edgeFalloff = 1 - Math.pow(Math.abs(offset) / halfSpan, 2);
            const halfLength = halfSpan * 0.92 * Math.max(0.2, edgeFalloff);

            const baseX = centerX + across.x * offset;
            const baseZ = centerZ + across.z * offset;

            structures.push({
              kind: 'cropRow',
              fromX: baseX - along.x * halfLength,
              fromZ: baseZ - along.z * halfLength,
              toX: baseX + along.x * halfLength,
              toZ: baseZ + along.z * halfLength,
              height: FARMLAND.ROW_HEIGHT,
              segments: FARMLAND.ROW_SEGMENTS,
              sampleGround,
              maxElevation: FARMLAND.MAX_ELEVATION,
              weight,
            });
          }

          // --- hedgerows ---------------------------------------------------

          /*
            Only the two low-side edges, so every interior hedge belongs to exactly one field. Emitting all
            four would draw each one twice, and two coincident lines are twice as bright under bloom.
          */
          const hedge = (fromX, fromZ, toX, toZ) => ({
            kind: 'hedgerow',
            fromX,
            fromZ,
            toX,
            toZ,
            lift: FARMLAND.BOUNDARY_LIFT,
            segments: FARMLAND.BOUNDARY_SEGMENTS,
            sampleGround,
            maxElevation: FARMLAND.MAX_ELEVATION,
            weight,
          });

          const hedgeChance = FARMLAND.BOUNDARY_CHANCE * weight;
          if (westHedgeRoll < hedgeChance) {
            structures.push(hedge(minX, minZ, minX, minZ + size));
          }
          if (northHedgeRoll < hedgeChance) {
            structures.push(hedge(minX, minZ, minX + size, minZ));
          }

          // --- farmstead ---------------------------------------------------

          if (farmsteadRoll < FARMLAND.FARMSTEAD_CHANCE * weight) {
            /*
              Tucked into a corner of the field rather than the middle, the way a farmstead sits at the edge of
              its land. Kept well inside so it never straddles a hedgerow.
            */
            const yardX = minX + lerp(size * 0.15, size * 0.32, farmsteadPlaceX);
            const yardZ = minZ + lerp(size * 0.15, size * 0.32, farmsteadPlaceZ);
            const facing = farmsteadFacingRoll * Math.PI * 2;

            if (!blocked(yardX, yardZ, FARMLAND.BARN_WIDTH)) {
              const groundAt = (x, z) => sampleGround(x, z);

              structures.push({
                kind: 'gableBuilding',
                centerX: yardX,
                centerZ: yardZ,
                width: FARMLAND.HOUSE_WIDTH,
                depth: FARMLAND.HOUSE_DEPTH,
                groundY: groundAt(yardX, yardZ),
                wallHeight: FARMLAND.HOUSE_WALL_HEIGHT,
                roofHeight: FARMLAND.HOUSE_ROOF_HEIGHT,
                facing,
                weight,
              });

              // Barn set off to one side, sharing the yard's orientation the way farm buildings do.
              const barnX = yardX + Math.cos(facing) * (FARMLAND.HOUSE_WIDTH + FARMLAND.BARN_WIDTH) * 0.6;
              const barnZ = yardZ + Math.sin(facing) * (FARMLAND.HOUSE_WIDTH + FARMLAND.BARN_WIDTH) * 0.6;

              // Silo beside the barn. Tallest thing in the yard, so it is what reads from furthest away.
              const siloX = barnX - Math.sin(facing) * (FARMLAND.BARN_DEPTH * 0.5 + FARMLAND.SILO_RADIUS * 2);
              const siloZ = barnZ + Math.cos(facing) * (FARMLAND.BARN_DEPTH * 0.5 + FARMLAND.SILO_RADIUS * 2);

              /*
                Both tested, not just the yard. The barn and the silo sit at offsets set by the yard's *facing*, so they can
                be tens of units away in an arbitrary direction — a dry yard with its barn in the lake was possible, and
                the yard test alone would never have seen it.
              */
              const outbuildingsClear =
                !blocked(barnX, barnZ, FARMLAND.BARN_WIDTH) &&
                !blocked(siloX, siloZ, FARMLAND.SILO_RADIUS * 2);

              if (outbuildingsClear) {
                structures.push({
                  kind: 'gableBuilding',
                  centerX: barnX,
                  centerZ: barnZ,
                  width: FARMLAND.BARN_WIDTH,
                  depth: FARMLAND.BARN_DEPTH,
                  groundY: groundAt(barnX, barnZ),
                  wallHeight: FARMLAND.BARN_WALL_HEIGHT,
                  roofHeight: FARMLAND.BARN_ROOF_HEIGHT,
                  facing,
                  weight,
                });

                structures.push({
                  kind: 'silo',
                  x: siloX,
                  z: siloZ,
                  groundY: groundAt(siloX, siloZ),
                  radius: FARMLAND.SILO_RADIUS,
                  height: FARMLAND.SILO_HEIGHT,
                  sides: FARMLAND.SILO_SIDES,
                  weight,
                });
              }
            }
          }

          // --- windpump ----------------------------------------------------

          if (windpumpRoll >= FARMLAND.WINDPUMP_CHANCE * weight) continue;

          const x = minX + lerp(size * 0.55, size * 0.85, windpumpPlaceX);
          const z = minZ + lerp(size * 0.55, size * 0.85, windpumpPlaceZ);

          const spot = sampleGroundDetail(x, z);
          if (spot.slope > FARMLAND.WINDPUMP_MAX_SLOPE) continue;

          /*
            Keep out of another biome's structures. Checked after every roll is consumed, so rejecting one
            windpump cannot shift the pattern of any other field.
          */
          if (blocked(x, z, FARMLAND.WINDPUMP_WHEEL_RADIUS)) continue;

          structures.push({
            kind: 'windpump',
            x,
            z,
            groundY: spot.elevation,
            height: FARMLAND.WINDPUMP_HEIGHT,
            baseWidth: FARMLAND.WINDPUMP_BASE_WIDTH,
            wheelRadius: FARMLAND.WINDPUMP_WHEEL_RADIUS,
            blades: FARMLAND.WINDPUMP_BLADES,
            facing: windpumpFacing * Math.PI * 2,
            weight,
          });
        }
      }

      /*
        The wind farms, collected last so they cannot shift any field's rolls. They are an installation on this
        biome rather than part of it — see `world/windFarm.js` — so their own siting is entirely separate from the
        field grid above, and they keep clear of other biomes' structures and of the railway through the same
        `isReserved` every other placer uses.
      */
      structures.push(...windFarms(region, { sampleWeight, sampleGroundDetail, isReserved }));

      return structures;
    };
  },

  /**
   * Publish the wind farms, so nothing else stands inside one.
   *
   * **Farmland's first reservation, and only the wind farms are in it.** Fields, hedgerows and farmsteads have
   * never needed one: a tree among crops is not a bug the way a tree inside a tower is. A row of nine turbines
   * is different — it is an installation with a footprint thousands of units long, and a forest growing through
   * it at a region boundary would be nonsense.
   *
   * Deliberately does *not* keep this biome's own fields out. A wind turbine standing in a crop is what a real
   * one does; it is the farmyard buildings that have to keep their distance, and those check it directly since a
   * biome never sees its own reservation.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number, radius: number, context: object) => boolean}
   */
  createReservation(seed) {
    return createWindFarmClearance(seed, 'farmland');
  },
};
